import { useQuery } from 'react-query'
import { proxy, useSnapshot } from 'valtio'
import { devtools } from 'valtio/utils'
import { apiConfig } from '../../../common/apiConfig'
import { AdminMailTemplatesApi, MailTemplate } from '../../../generated'
import { onError } from '../user/UserStore'

export interface TemplateOptionsStore {
  acceptEntryTemplates?: Array<MailTemplate>
  approvalTemplates?: Array<MailTemplate>
  approvalCancelTemplates?: Array<MailTemplate>
  relayTemplates?: Array<MailTemplate>
  relayCancelTemplates?: Array<MailTemplate>
  respondReentryTemplates?: Array<MailTemplate>
  respondEntryTemplates?: Array<MailTemplate>
  notifyApprovalNumberTemplates?: Array<MailTemplate>
  notifyRejectTemplates?: Array<MailTemplate>
  nonRegularRelayRequestTemplates?: Array<MailTemplate>
  nonRegularRelayCancelTemplates?: Array<MailTemplate>
}

export const templateOptionsStore = proxy<TemplateOptionsStore>({
  acceptEntryTemplates: undefined,
  approvalTemplates: undefined,
  approvalCancelTemplates: undefined,
  relayTemplates: undefined,
  relayCancelTemplates: undefined,
  respondReentryTemplates: undefined,
  respondEntryTemplates: undefined,
  notifyApprovalNumberTemplates: undefined,
  notifyRejectTemplates: undefined,
  nonRegularRelayRequestTemplates: undefined,
  nonRegularRelayCancelTemplates: undefined,
})

const api = new AdminMailTemplatesApi(apiConfig)

function toOptions(src: MailTemplate[]) {
  return src.map((t) => ({ ...t, value: t.id, label: t.name }))
}

async function load(id: number) {
  const templates = await api.loadMailTemplates(id)
  return toOptions(templates)
}

async function loadByCode(code: string) {
  const templates = await api.loadMailTemplatesByCode(code)
  return toOptions(templates)
}

export async function loadAcceptEntryTemplates() {
  await new AdminMailTemplatesApi(apiConfig)
    .loadMailTemplates(1)
    .then((res) => {
      templateOptionsStore.acceptEntryTemplates = res
    })
    .catch((error) => console.log('error', error))
}

export function getAcceptEntryTemplateOptions(templates?: Array<MailTemplate>) {
  if (!templates) return
  return templates.map((c) => ({
    ...c,
    value: c.id,
    label: c.name,
  }))
}

export async function loadApprovalTemplates() {
  await new AdminMailTemplatesApi(apiConfig)
    .loadMailTemplates(2)
    .then((res) => {
      templateOptionsStore.approvalTemplates = res
    })
    .catch((error) => console.log('error', error))
  await new AdminMailTemplatesApi(apiConfig)
    .loadMailTemplates(4)
    .then((res) => {
      templateOptionsStore.approvalCancelTemplates = res
    })
    .catch((error) => console.log('error', error))
}

export function getApprovalTemplateOptions() {
  if (!templateOptionsStore.approvalTemplates) return
  return templateOptionsStore.approvalTemplates.map((c) => ({
    ...c,
    value: c.id,
    label: c.name,
  }))
}

export function getApprovalCancelTemplateOptions() {
  if (!templateOptionsStore.approvalCancelTemplates) return
  return templateOptionsStore.approvalCancelTemplates.map((c) => ({
    ...c,
    value: c.id,
    label: c.name,
  }))
}

export function useRelayRequestTemplates() {
  const { data } = useQuery('useRelayRequestTemplates', () => load(5), { onError })
  return data ?? []
}

export function useRelayCancelTemplates() {
  const { data } = useQuery('useRelayCancelTemplates', () => load(6), { onError })
  return data ?? []
}

export async function loadReturnApplicantTemplates() {
  await new AdminMailTemplatesApi(apiConfig)
    .loadMailTemplates(8)
    .then((res) => {
      templateOptionsStore.respondReentryTemplates = res
    })
    .catch((error) => console.log('error', error))
  await new AdminMailTemplatesApi(apiConfig)
    .loadMailTemplates(7)
    .then((res) => {
      templateOptionsStore.respondEntryTemplates = res
    })
    .catch((error) => console.log('error', error))
}

export function getRespondReentryTemplatesOptions() {
  if (!templateOptionsStore.respondReentryTemplates) return
  return templateOptionsStore.respondReentryTemplates.map((c) => ({
    ...c,
    value: c.id,
    label: c.name,
  }))
}

export function getRespondEntryTemplatesOptions() {
  if (!templateOptionsStore.respondEntryTemplates) return
  return templateOptionsStore.respondEntryTemplates.map((c) => ({
    ...c,
    value: c.id,
    label: c.name,
  }))
}

export async function loadNotifyApprovalNumberTemplates() {
  await new AdminMailTemplatesApi(apiConfig)
    .loadMailTemplates(12)
    .then((res) => {
      templateOptionsStore.notifyApprovalNumberTemplates = res
    })
    .catch((error) => console.log('error', error))
}

export function getNotifyApprovalNumberOptions() {
  if (!templateOptionsStore.notifyApprovalNumberTemplates) return
  return templateOptionsStore.notifyApprovalNumberTemplates.map((c) => ({
    ...c,
    value: c.id,
    label: c.name,
  }))
}

export async function loadNotifyRejectTemplates() {
  await new AdminMailTemplatesApi(apiConfig)
    .loadMailTemplates(13)
    .then((res) => {
      templateOptionsStore.notifyRejectTemplates = res
    })
    .catch((error) => console.log('error', error))
}

export function getNotifyRejectOptions() {
  if (!templateOptionsStore.notifyRejectTemplates) return
  return templateOptionsStore.notifyRejectTemplates.map((c) => ({
    ...c,
    value: c.id,
    label: c.name,
  }))
}

if (import.meta.env.DEV) {
  devtools(templateOptionsStore, 'TemplateOptionsStore')
}

// NOTE virtio useSnapshot の返す型が一致しないので cast する
export function useTemplateOptionsStore(): TemplateOptionsStore {
  return useSnapshot(templateOptionsStore) as unknown as TemplateOptionsStore
}

// purposes
// [
//   {
//     "id": 1,
//     "code": "AcceptEntry",
//     "name": "申請受付"
//   },
//   {
//     "id": 2,
//     "code": "ApproveRequest",
//     "name": "承認依頼"
//   },
//   {
//     "id": 4,
//     "code": "ApproveCancel",
//     "name": "承認依頼キャンセル"
//   },
//   {
//     "id": 5,
//     "code": "RelayRequest",
//     "name": "部局への検討依頼"
//   },
//   {
//     "id": 6,
//     "code": "RelayCancel",
//     "name": "部局への検討依頼キャンセル"
//   },
//   {
//     "id": 7,
//     "code": "RespondEntry",
//     "name": "結果通知ブルー"
//   },
//   {
//     "id": 8,
//     "code": "RespondReentry",
//     "name": "結果通知オレンジ"
//   }
// ]

export function useNonRegularRelayRequestTemplates() {
  const { data } = useQuery('useNonRegularRelayRequestTemplates', () => loadByCode('NonRegularRelayRequest'), { onError })
  return data ?? []
}

export function useNonRegularRespondReentryTemplates() {
  const { data } = useQuery('useNonRegularRespondReentryTemplates', () => loadByCode('NonRegularRespondReentry'), { onError })
  return data ?? []
}

// 非正規支援室依頼テンプレート
// export async function loadAdminNonRegularRelayRequestTemplates() {
//   await new AdminMailTemplatesApi(apiConfig)
//     .loadMailTemplatesByCode('NonRegularRelayRequest')
//     .then((res: Array<MailTemplate>) => {
//       templateOptionsStore.nonRegularRelayRequestTemplates = res
//     })
//     .catch((error: any) => console.log('error', error))
// }

// export function getAdminNonRegularRelayRequestTemplates() {
//   if (!templateOptionsStore.nonRegularRelayRequestTemplates) return
//   return templateOptionsStore.nonRegularRelayRequestTemplates.map((c) => ({
//     ...c,
//     value: c.id,
//     label: c.name,
//   }))
// }

// 非正規支援室依頼キャンセルテンプレート
export async function loadAdminNonRegularRelayCancelTemplates() {
  await new AdminMailTemplatesApi(apiConfig)
    .loadMailTemplatesByCode('NonRegularRelayCancel')
    .then((res: Array<MailTemplate>) => {
      templateOptionsStore.nonRegularRelayCancelTemplates = res
    })
    .catch((error: any) => console.log('error', error))
}

export function getAdminNonRegularRelayCancelTemplates() {
  if (!templateOptionsStore.nonRegularRelayCancelTemplates) return
  return templateOptionsStore.nonRegularRelayCancelTemplates.map((c) => ({
    ...c,
    value: c.id,
    label: c.name,
  }))
}
